/**
 * Synchronous brand resolver.
 *
 * `main.tsx`'s `Root()` builds the Mobile Wallet Adapter `appIdentity` (the
 * name/icon a wallet shows in its connect popup) and sets `document.title` —
 * both run *above* `LaunchProvider` in the tree, so they can't read the React
 * launch context. This module mirrors `readLaunchId()` from LaunchProvider and
 * returns a brand bundle synchronously at boot.
 *
 * Wallets cannot be told how to draw their own approval chrome (security
 * boundary), but they DO read the dapp's name + icon + origin — this is the
 * only lever we have over what that popup says, so we drive it from here.
 *
 * Per-launch overrides: a launch's `/launches/<id>/theme.json` may carry a
 * `brand` block (parsed in LaunchProvider) that the in-app connect modal
 * prefers over this static default. The default is Crownclash because the
 * production site (crownclash.xyz) loads without a `?launch=` param.
 */

export interface BrandAccent {
  /** Highlight / "allowed" green — pump.fun-style lime. */
  primary: string;
  /** Primary CTA (Connect button) — pump.fun-style yellow. */
  cta: string;
  /** Page / backdrop. */
  bg: string;
  /** Modal card surface. */
  card: string;
  /** Primary text. */
  text: string;
  /** Muted text. */
  subtext: string;
  /** Hairline borders. */
  border: string;
}

export interface Brand {
  /** Launch slug this brand belongs to. */
  id: string;
  /** Display name shown in the wallet popup + connect modal header. */
  appName: string;
  /** One-line pitch under the name in the connect modal. */
  tagline: string;
  /** Absolute site path to the logo/icon (also used as favicon). */
  iconUrl: string;
  /** Canonical site URL (display only). */
  url: string;
  accent: BrandAccent;
}

const PUMP_ACCENT: BrandAccent = {
  primary: "#3fd07a",
  cta: "#facc15",
  bg: "#0a0a10",
  card: "#13131a",
  text: "#e8e8ef",
  subtext: "#9aa0b4",
  border: "#23232e",
};

const CROWNCLASH: Brand = {
  id: "crownclash",
  appName: "Crownclash",
  tagline: "Last touch wins the pot.",
  iconUrl: "/favicon.svg",
  url: "https://crownclash.xyz",
  accent: PUMP_ACCENT,
};

const BRANDS: Record<string, Brand> = {
  crownclash: CROWNCLASH,
  // The historical default content variant reuses the Crownclash chrome so the
  // bare site (no ?launch=) is fully branded.
  main: { ...CROWNCLASH, id: "main" },
};

/** Default brand when the launch id is unknown / absent. */
export const DEFAULT_BRAND: Brand = CROWNCLASH;

/** Read `?launch=<id>` from the URL (slug-safe), defaulting to "crownclash". */
function readLaunchId(): string {
  if (typeof window === "undefined") return "crownclash";
  try {
    const raw = new URL(window.location.href).searchParams.get("launch");
    if (raw && /^[A-Za-z0-9_-]{1,64}$/.test(raw)) return raw;
  } catch {
    /* malformed URL — fall through to default */
  }
  return "crownclash";
}

/**
 * Resolve the boot-time brand from the `?launch=` param. Always returns a
 * usable brand — unknown launches fall back to the Crownclash default (a
 * launch-specific `brand` block from its theme.json can still override the
 * in-app modal at runtime via LaunchProvider).
 */
export function resolveBrand(): Brand {
  return BRANDS[readLaunchId()] ?? DEFAULT_BRAND;
}
